<script lang="ts">
  import ModalDialog from "@kontextwerk/web-sdk/ui/overlay/ModalDialog.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiMagnifyPlusOutline, mdiFitToScreenOutline, mdiImageSizeSelectActual } from "@mdi/js"
  import type { GuideImage } from "../../../../../../../../../../api/kehrblatt/learning-guide"

  const { image } = $props<{ image: GuideImage }>()
  let expanded = $state(false)
  let fit = $state(true)
  let failed = $state(false)
  let naturalWidth = $state(0)
  let naturalHeight = $state(0)
  let viewportWidth = $state(0)
  let viewportHeight = $state(0)
  const crop = $derived(image.focusRegion)
  const cropAspect = $derived(
    crop && naturalWidth && naturalHeight
      ? (naturalWidth * crop.width) / (naturalHeight * crop.height)
      : null,
  )
  const fullSize = $derived(naturalWidth ? `${naturalWidth}px` : "auto")
  const markerInset = $derived(image.markers?.length ? 28 : 0)
  // The bitmap and its markers share one scale, constrained by both viewer dimensions.
  const fittedWidth = $derived(
    naturalWidth && naturalHeight && viewportWidth && viewportHeight
      ? `${Math.min(naturalWidth, viewportWidth, (Math.max(0, viewportHeight - markerInset) * naturalWidth) / naturalHeight)}px`
      : "100%",
  )
  const inlineWidth = $derived(
    naturalWidth ? `${(naturalWidth * (crop?.width ?? 100)) / 100}px` : undefined,
  )

  function loaded(event: Event) {
    const element = event.currentTarget as HTMLImageElement
    naturalWidth = element.naturalWidth
    naturalHeight = element.naturalHeight
  }
  function open() {
    fit = true
    expanded = true
  }
</script>

{#snippet markedImage(cropped: boolean)}
    {@const region = cropped && cropAspect && crop ? crop : { x: 0, y: 0, width: 100, height: 100 }}
    <span class={["image-surface", { annotated: Boolean(image.markers?.length) }]}>
        <span class="image-viewport">
            <span
        class={["image-bitmap", { cropped: cropped && Boolean(cropAspect) }]}
        style:aspect-ratio={cropped && cropAspect ? String(cropAspect) : undefined}
      >
                <img
          src={image.src}
          alt={image.alt}
          loading={expanded ? "eager" : "lazy"}
          decoding="async"
          onload={loaded}
          onerror={() => (failed = true)}
          style:width={cropped && cropAspect ? `${10000 / region.width}%` : "100%"}
          style:left={cropped && cropAspect ? `${(-100 * region.x) / region.width}%` : undefined}
          style:top={cropped && cropAspect ? `${(-100 * region.y) / region.height}%` : undefined}
        />
            </span>
            {#each image.markers ?? [] as marker (marker.number)}
                <span
          class="image-marker"
          aria-hidden="true"
          style:left={`${(100 * (marker.x - region.x)) / region.width}%`}
          style:top={`${(100 * (marker.y - region.y)) / region.height}%`}
          style:width={`${(100 * marker.width) / region.width}%`}
          style:height={`${(100 * marker.height) / region.height}%`}
          ><span class="marker-number">{marker.number}</span></span
        >
            {/each}
        </span>
    </span>
{/snippet}

<figure style:max-width={inlineWidth}>
    {#if failed}
        <div class="image-failed" role="status">
            <p>Das Bild konnte nicht geladen werden.</p>
            <p>{image.alt}</p>
            <button type="button" onclick={() => (failed = false)}>Bild erneut laden</button>
        </div>
    {:else}
        <button
      class="image-open"
      type="button"
      aria-label={`Bild vergrößern: ${image.alt}`}
      onclick={open}
    >
            {@render markedImage(true)}
            <span class="image-open-label"
        ><Icon path={mdiMagnifyPlusOutline} size="1.1rem" /> Vergrößern</span
      >
        </button>
    {/if}
    {#if image.caption}<figcaption>{image.caption}</figcaption>{/if}
</figure>

{#if expanded && !failed}
    <ModalDialog title="Abbildung" appearance="detail" size="xl" onClose={() => (expanded = false)}>
        {#snippet descriptionSnippet()}<p class="zoom-description">{image.alt}</p>{/snippet}
        {#snippet navigationSnippet()}
        <div class="zoom-controls" role="group" aria-label="Bildgröße">
            <button type="button" aria-pressed={fit} onclick={() => (fit = true)}>
                <Icon path={mdiFitToScreenOutline} size="1.1rem" /> Einpassen
            </button>
            <button type="button" aria-pressed={!fit} onclick={() => (fit = false)}>
                <Icon path={mdiImageSizeSelectActual} size="1.1rem" /> 100 %
            </button>
        </div>
        {/snippet}
        <!-- svelte-ignore a11y_no_noninteractive_tabindex (The original-size image must be scrollable with the keyboard.) -->
        <div
      class={["zoom-scroll", { fitted: fit }]}
      role="region"
      aria-label="Vergrößertes Bild"
      tabindex="0"
      bind:clientWidth={viewportWidth}
      bind:clientHeight={viewportHeight}
    >
            <div class="zoom-image" style:width={fit ? fittedWidth : fullSize}>
                {@render markedImage(false)}
            </div>
        </div>
        {#snippet footerSnippet()}
          {#if image.caption}<p class="zoom-caption">{image.caption}</p>{/if}
        {/snippet}
    </ModalDialog>
{/if}

<style>
  figure {
    margin: 1rem 0 1.5rem;
    min-width: 0;
  }
  .image-open {
    display: block;
    width: 100%;
    padding: 0;
    border: 1px solid #d0d7da;
    background: #f6f7f7;
    color: inherit;
    cursor: zoom-in;
    text-align: left;
  }
  .image-open:focus-visible,
  button:focus-visible,
  .zoom-scroll:focus-visible {
    outline: 3px solid #7c2024;
    outline-offset: 3px;
  }
  .image-surface {
    display: block;
    overflow: hidden;
  }
  .image-surface.annotated {
    /* Keep edge markers outside their target without clipping their number. */
    padding-top: 28px;
  }
  .image-viewport,
  .image-bitmap {
    position: relative;
    display: block;
    width: 100%;
    transition: none;
  }
  .image-bitmap {
    overflow: hidden;
  }
  .image-viewport img {
    display: block;
    height: auto;
    max-width: none;
    transition: none;
  }
  .image-bitmap.cropped img {
    position: absolute;
  }
  .image-marker {
    position: absolute;
    border: 2px solid #c72e27;
    box-sizing: border-box;
    pointer-events: none;
    /* Match the bitmap immediately when its crop or display size changes. */
    transition: none;
  }
  .marker-number {
    position: absolute;
    bottom: calc(100% + 2px);
    left: -2px;
    min-width: 1.5rem;
    height: 1.5rem;
    padding: 0 0.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: #a41f1b;
    font-weight: 700;
    font-size: 15px;
    box-sizing: border-box;
  }
  .image-open-label {
    display: flex;
    gap: 0.35rem;
    align-items: center;
    padding: 0.45rem 0.65rem;
    font-size: 15px;
  }
  figcaption,
  .zoom-caption,
  .zoom-description {
    margin: 0.5rem 0;
    font-size: 16px;
    line-height: 1.5;
    color: inherit;
    overflow-wrap: anywhere;
  }
  .image-failed {
    border: 1px solid #d0d7da;
    padding: 1rem;
  }
  .zoom-controls {
    display: flex;
    gap: 0.5rem;
    padding: 0.75rem var(--kw-app-content-inset, 24px);
    flex-wrap: wrap;
    color: var(--text-invers-100);
  }
  .zoom-controls button,
  .image-failed button {
    display: inline-flex;
    gap: 0.4rem;
    align-items: center;
    padding: 0.5rem 0.75rem;
    border: 1px solid #acbac1;
    font: inherit;
    color: inherit;
    background: white;
    cursor: pointer;
    transition: none;
  }
  .zoom-controls button[aria-pressed="true"] {
    color: white;
    background: #344e5a;
  }
  .zoom-scroll {
    overflow: auto;
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    box-sizing: border-box;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
  }
  .zoom-scroll.fitted {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .zoom-image {
    max-width: none;
    flex: 0 0 auto;
    transition: none;
  }
</style>
